/**
 * Reliable getUserMedia for PWA home-screen, MAX WebView, and Safari.
 * Auto-start from useEffect is often blocked (no user gesture) — callers must
 * pass a stream obtained in a click handler on first grant.
 */

export function isCameraApiAvailable(): boolean {
  return (
    typeof navigator !== "undefined" &&
    Boolean(navigator.mediaDevices?.getUserMedia) &&
    (typeof window === "undefined" || window.isSecureContext)
  );
}

export function isNotAllowedError(err: unknown): boolean {
  if (err instanceof DOMException && err.name === "NotAllowedError") return true;
  const msg = err instanceof Error ? err.message : String(err);
  return /NotAllowed|PermissionDenied|PermissionDismissed|denied/i.test(msg);
}

export function isBusyCameraError(err: unknown): boolean {
  if (err instanceof DOMException && (err.name === "NotReadableError" || err.name === "AbortError")) {
    return true;
  }
  const msg = err instanceof Error ? err.message : String(err);
  return /NotReadable|AbortError|Could not start video|in use|Device in use/i.test(msg);
}

const CONSTRAINT_CHAIN: MediaStreamConstraints[] = [
  {
    audio: false,
    video: {
      facingMode: { ideal: "environment" },
      width: { ideal: 1280 },
      height: { ideal: 720 },
    },
  },
  { audio: false, video: { facingMode: { ideal: "environment" } } },
  { audio: false, video: true },
];

function wait(ms: number) {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

export async function openCameraStream(options?: { timeoutMs?: number }): Promise<MediaStream> {
  if (!isCameraApiAvailable()) {
    throw new Error("camera_api_unavailable");
  }

  const timeoutMs = options?.timeoutMs ?? 12000;
  const open = async (): Promise<MediaStream> => {
    let lastErr: unknown;
    for (const constraints of CONSTRAINT_CHAIN) {
      for (let attempt = 0; attempt < 3; attempt++) {
        try {
          return await navigator.mediaDevices.getUserMedia(constraints);
        } catch (err) {
          lastErr = err;
          if (isNotAllowedError(err)) throw err;
          if (err instanceof DOMException && err.name === "OverconstrainedError") break;
          if (isBusyCameraError(err) || err instanceof DOMException) {
            await wait(280 * (attempt + 1));
            continue;
          }
          break;
        }
      }
    }
    throw lastErr instanceof Error ? lastErr : new Error("camera_open_failed");
  };

  return await Promise.race([
    open(),
    new Promise<MediaStream>((_, reject) => {
      window.setTimeout(() => reject(new Error("camera_timeout")), timeoutMs);
    }),
  ]);
}

export async function attachStreamToVideo(
  video: HTMLVideoElement,
  stream: MediaStream,
): Promise<void> {
  video.setAttribute("playsinline", "true");
  video.setAttribute("webkit-playsinline", "true");
  video.muted = true;
  video.defaultMuted = true;
  video.autoplay = true;
  video.playsInline = true;
  // Avoid mirror / transform quirks that can blank some WebViews.
  video.style.transform = "none";
  video.style.objectFit = "cover";

  if (video.srcObject !== stream) {
    video.srcObject = stream;
  }

  await new Promise<void>((resolve) => {
    if (video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA && video.videoWidth > 0) {
      resolve();
      return;
    }
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      video.removeEventListener("loadeddata", finish);
      video.removeEventListener("playing", finish);
      resolve();
    };
    video.addEventListener("loadeddata", finish);
    video.addEventListener("playing", finish);
    window.setTimeout(finish, 2500);
  });

  try {
    await video.play();
  } catch {
    // Autoplay may wait until a later tap; stream is still attached.
  }
}

export function stopMediaStream(stream: MediaStream | null | undefined): void {
  if (!stream) return;
  for (const track of stream.getTracks()) {
    try {
      track.stop();
    } catch {
      /* ignore */
    }
  }
}

export function isStreamLive(stream: MediaStream | null | undefined): boolean {
  return Boolean(stream?.active && stream.getVideoTracks().some((t) => t.readyState === "live"));
}
